import React, { useState, useEffect, useRef } from 'react';
import { Moon, Sun, Maximize, Minimize, BarChart3, Settings, Copy, Download, FileText, Zap, Eye, RefreshCw, ChevronDown, ChevronUp, Info, CheckCircle, AlertCircle, X, TrendingUp, Activity, Clock, HardDrive } from 'lucide-react';
import { useUserTracking } from './../../../../../contexts/UserTrackingContext';

// Type definitions
interface AnalyticsData {
  conversions: number;
  downloads: number;
  copies: number;
  avgFileSize: number;
  recentActivity: ActivityItem[];
}

interface ActivityItem {
  action: string;
  time: string;
  [key: string]: any;
}

interface TrackingEvent {
  event_category: string;
  event_label: string;
  page_location?: string;
  timestamp?: string;
  value?: number;
  [key: string]: any;
}

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
}

interface AnalyticsModalProps {
  isOpen: boolean;
  onClose: () => void;
  analytics: AnalyticsData;
}

type YamlFormat = 'default' | 'json' | 'yaml1.1' | 'yaml1.2';
type IndentSize = 2 | 4 | 8;

// Mock data for demonstration
const sampleData = {
  "name": "John Doe",
  "age": 30,
  "email": "john.doe@example.com",
  "address": {
    "street": "123 Main St",
    "city": "New York",
    "zipCode": "10001",
    "country": "USA"
  },
  "hobbies": ["reading", "programming", "hiking", "photography"],
  "isActive": true,
  "preferences": {
    "theme": "dark",
    "notifications": true,
    "language": "en"
  },
  "metadata": {
    "createdAt": "2024-01-15T10:30:00Z",
    "updatedAt": "2024-06-08T14:22:00Z",
    "version": "1.2.3"
  }
};


// SEO Head component (mock implementation of react-helmet-async)
const SEOHead: React.FC<SEOProps> = ({ title, description, keywords }) => {
  useEffect(() => {
    document.title = title;

    // Update meta tags
    const updateMeta = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const updateProperty = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    updateMeta('description', description);
    updateMeta('keywords', keywords);
    updateProperty('og:title', title);
    updateProperty('og:description', description);
    updateProperty('og:type', 'website');
    updateMeta('twitter:card', 'summary');
    updateMeta('twitter:title', title);
    updateMeta('twitter:description', description);
  }, [title, description, keywords]);

  return null;
};

// Analytics Dashboard Modal
const AnalyticsModal: React.FC<AnalyticsModalProps> = ({ isOpen, onClose, analytics }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
                <BarChart3 className="w-6 h-6 mr-3 text-blue-600 dark:text-blue-400" />
                Analytics Dashboard
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mt-1">Track your conversion activities and usage patterns</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-6 rounded-xl border border-blue-200 dark:border-blue-800">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{analytics.conversions}</div>
                  <div className="text-sm text-blue-700 dark:text-blue-300 mt-1">Total Conversions</div>
                </div>
                <TrendingUp className="w-8 h-8 text-blue-500 dark:text-blue-400" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-6 rounded-xl border border-green-200 dark:border-green-800">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-green-600 dark:text-green-400">{analytics.downloads}</div>
                  <div className="text-sm text-green-700 dark:text-green-300 mt-1">Downloads</div>
                </div>
                <Download className="w-8 h-8 text-green-500 dark:text-green-400" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-6 rounded-xl border border-purple-200 dark:border-purple-800">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">{analytics.copies}</div>
                  <div className="text-sm text-purple-700 dark:text-purple-300 mt-1">Copies</div>
                </div>
                <Copy className="w-8 h-8 text-purple-500 dark:text-purple-400" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 p-6 rounded-xl border border-orange-200 dark:border-orange-800">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">{Math.round(analytics.avgFileSize)}</div>
                  <div className="text-sm text-orange-700 dark:text-orange-300 mt-1">Avg Size (KB)</div>
                </div>
                <HardDrive className="w-8 h-8 text-orange-500 dark:text-orange-400" />
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Activity className="w-5 h-5 mr-2 text-gray-600 dark:text-gray-400" />
              Recent Activity
            </h3>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {analytics.recentActivity.length > 0 ? analytics.recentActivity.map((activity: ActivityItem, index: number) => (
                <div key={index} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center space-x-3">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{activity.action}</span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{activity.time}</span>
                </div>
              )) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No activity recorded yet</p>
                  <p className="text-sm">Start converting to see your activity here!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
const JsonToYamlConverter: React.FC = () => {
  // Theme and UI states
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);

  // Core conversion states
  const [jsonInput, setJsonInput] = useState<string>('');
  const [yamlOutput, setYamlOutput] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [fileName, setFileName] = useState<string>('data');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isYamlGenerated, setIsYamlGenerated] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);

  // Basic options
  const [yamlFormat, setYamlFormat] = useState<YamlFormat>('default');
  const [indentSize, setIndentSize] = useState<IndentSize>(2);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);

  // Advanced options
  const [quoteStrings, setQuoteStrings] = useState<boolean>(false);
  const [flowLevel, setFlowLevel] = useState<number>(-1);
  const [lineWidth, setLineWidth] = useState<number>(80);
  const [allowUnicode, setAllowUnicode] = useState<boolean>(true);
  const [sortKeys, setSortKeys] = useState<boolean>(false);
  const [skipInvalid, setSkipInvalid] = useState<boolean>(false);
  const [preserveQuotes, setPreserveQuotes] = useState<boolean>(false);
  const [explicitStart, setExplicitStart] = useState<boolean>(false);
  const [explicitEnd, setExplicitEnd] = useState<boolean>(false);

  // Analytics state
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    conversions: 0,
    downloads: 0,
    copies: 0,
    avgFileSize: 0,
    recentActivity: []
  });

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const { trackEvent } = useUserTracking();

  // Initialize theme and tracking
  useEffect(() => {
    const savedTheme = localStorage.getItem('json-yaml-theme');
    const savedAnalytics = localStorage.getItem('json-yaml-analytics');

    if (savedTheme === 'dark') {
      setIsDarkMode(true);
      document.documentElement.classList.add('dark');
    }

    if (savedAnalytics) {
      try {
        setAnalytics(JSON.parse(savedAnalytics));
      } catch (e) {
        console.error('Failed to parse saved analytics:', e);
      }
    }

    // Track page load
    trackEvent('page_load', {
      event_category: 'engagement',
      event_label: 'JSON to YAML Converter',
      page_location: window.location.href,
    });

    // Track fullscreen change
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [trackEvent]);

  // Save analytics to localStorage
  useEffect(() => {
    localStorage.setItem('json-yaml-analytics', JSON.stringify(analytics));
  }, [analytics]);

  // Toggle theme
  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);

    if (newTheme) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('json-yaml-theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('json-yaml-theme', 'light');
    }

    trackEvent('theme_toggle', {
      event_category: 'ui_interaction',
      event_label: newTheme ? 'Dark Mode' : 'Light Mode',
      page_location: window.location.href,
    });
  };

  // Toggle fullscreen
  const toggleFullscreen = async () => {
    try {
      if (!isFullscreen) {
        await containerRef.current?.requestFullscreen?.();
      } else {
        await document.exitFullscreen();
      }

      trackEvent('fullscreen_toggle', {
        event_category: 'ui_interaction',
        event_label: !isFullscreen ? 'Enter Fullscreen' : 'Exit Fullscreen',
        page_location: window.location.href,
      });
    } catch (error) {
      console.error('Fullscreen error:', error);
    }
  };

  // Update analytics
  const updateAnalytics = (action: string, data: { fileSize?: number;[key: string]: any } = {}) => {
    const timestamp = new Date().toLocaleTimeString();
    const activity: ActivityItem = {
      action: action.charAt(0).toUpperCase() + action.slice(1),
      time: timestamp,
      ...data
    };

    setAnalytics(prev => {
      const newAnalytics = { ...prev };

      // Update counters
      if (action === 'conversion') {
        newAnalytics.conversions = prev.conversions + 1;
        if (data.fileSize) {
          newAnalytics.avgFileSize = prev.conversions === 0 ?
            data.fileSize :
            (prev.avgFileSize * prev.conversions + data.fileSize) / (prev.conversions + 1);
        }
      } else if (action === 'download') {
        newAnalytics.downloads = prev.downloads + 1;
      } else if (action === 'copy') {
        newAnalytics.copies = prev.copies + 1;
      }

      // Update recent activity
      newAnalytics.recentActivity = [activity, ...prev.recentActivity.slice(0, 19)]; // Keep last 20

      return newAnalytics;
    });
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setJsonInput(value);
    setError('');
    setIsYamlGenerated(false);
    setYamlOutput('');

    trackEvent('input_change', {
      event_category: 'user_interaction',
      event_label: 'JSON Input Modified',
      page_location: window.location.href,
    });
  };

  // Use example data
  const useExampleData = () => {
    const exampleJson = JSON.stringify(sampleData, null, 2);
    setJsonInput(exampleJson);
    setError('');
    setIsYamlGenerated(false);
    setYamlOutput('');

    trackEvent('use_example', {
      event_category: 'user_interaction',
      event_label: 'Example Data Used',
      page_location: window.location.href,
    });
  };

  // Beautify JSON
  const beautifyJson = () => {
    try {
      if (!jsonInput.trim()) {
        setError('Please enter JSON data to format');
        return;
      }

      const parsedJson = JSON.parse(jsonInput);
      let beautified: string;

      if (sortKeys) {
        const sortObject = (obj: any): any => {
          if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) return obj;
          return Object.keys(obj).sort().reduce((result: any, key: string) => {
            result[key] = sortObject(obj[key]);
            return result;
          }, {});
        };
        beautified = JSON.stringify(sortObject(parsedJson), null, indentSize);
      } else {
        beautified = JSON.stringify(parsedJson, null, indentSize);
      }

      setJsonInput(beautified);
      setError('');

      trackEvent('beautify_json', {
        event_category: 'user_interaction',
        event_label: 'JSON Beautified',
        page_location: window.location.href,
      });
    } catch (e) {
      const errorMessage = `Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`;
      setError(errorMessage);

      trackEvent('beautify_error', {
        event_category: 'error',
        event_label: errorMessage,
        page_location: window.location.href,
      });
    }
  };

  // Validate JSON
  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Clear all
  const clearAll = () => {
    setJsonInput('');
    setYamlOutput('');
    setError('');
    setFileName('data');
    setYamlFormat('default');
    setIndentSize(2);
    setIsYamlGenerated(false);
    setQuoteStrings(false);
    setFlowLevel(-1);
    setLineWidth(80);
    setAllowUnicode(true);
    setSortKeys(false);
    setSkipInvalid(false);
    setPreserveQuotes(false);
    setExplicitStart(false);
    setExplicitEnd(false);

    trackEvent('clear_all', {
      event_category: 'user_interaction',
      event_label: 'All Fields Cleared',
      page_location: window.location.href,
    });
  };

  // YAML conversion helpers
  const getIndent = (level: number): string => ' '.repeat(indentSize * level);

  const needsQuotes = (str: string): boolean => {
    if (quoteStrings) return true;
    if (preserveQuotes && (str.startsWith('"') || str.startsWith("'"))) return true;

    const specialValues = [
      'true', 'false', 'yes', 'no', 'on', 'off',
      'null', 'undefined', '~', 'y', 'n', 'Y', 'N'
    ];

    if (specialValues.includes(str.toLowerCase())) return true;
    if (!isNaN(Number(str)) && str.trim() !== '') return true;

    const needsQuotePattern = /[:{}\[\],&*#?|<>=!%@`\\]/;
    if (needsQuotePattern.test(str)) return true;

    if (str.trim() !== str) return true;
    if (str.includes('\n')) return true;

    return false;
  };

  const valueToYaml = (value: any, level = 0, isArrayItem = false): string => {
    if (value === null || value === undefined) {
      return skipInvalid ? '' : 'null';
    }

    if (typeof value === 'function' && skipInvalid) {
      return '';
    }

    if (typeof value === 'string') {
      if (!allowUnicode) {
        value = value.replace(/[\u0080-\uFFFF]/g, (match: string) =>
          '\\u' + ('0000' + match.charCodeAt(0).toString(16)).substr(-4)
        );
      }

      if (needsQuotes(value)) {
        const escaped = value
          .replace(/\\/g, '\\\\')
          .replace(/"/g, '\\"')
          .replace(/\n/g, '\\n')
          .replace(/\r/g, '\\r')
          .replace(/\t/g, '\\t');
        return `"${escaped}"`;
      }
      return value;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    if (Array.isArray(value)) {
      if (value.length === 0) return '[]';

      // Flow style (inline) for arrays
      if (flowLevel >= 0 && level >= flowLevel) {
        const items: string = value
          .map((item: any) => valueToYaml(item, level + 1, true))
          .filter((item: string) => item !== '') // Remove skipped invalid values
          .join(', ');
        return `[${items}]`;
      }

      // Block style for arrays
      const indent = getIndent(level);
      const nextIndent = getIndent(level + 1);

      return '\n' + value
        .map((item: any) => {
          const itemStr: string = valueToYaml(item, level + 1, true);
          if (itemStr === '') return ''; // Skip invalid values

          if (itemStr.includes('\n')) {
            const lines: string[] = itemStr.split('\n');
            const firstLine: string = lines[0];
            const restLines: string = lines.slice(1).map((line: string) => `${nextIndent}${line}`).join('\n');
            return `${indent}- ${firstLine}\n${restLines}`;
          }
          return `${indent}- ${itemStr}`;
        })
        .filter((item: string) => item !== '')
        .join('\n');
    }

    if (typeof value === 'object') {
      const keys = Object.keys(value);

      if (keys.length === 0) return '{}';

      // Sort keys if option is enabled
      const sortedKeys = sortKeys ? keys.sort() : keys;

      // Flow style (inline) for objects
      if (flowLevel >= 0 && level >= flowLevel) {
        const props: string = sortedKeys
          .map((key: string) => {
            const k = needsQuotes(key) ? `"${key}"` : key;
            const v: string = valueToYaml(value[key], level + 1);
            return v !== '' ? `${k}: ${v}` : '';
          })
          .filter((prop: string) => prop !== '')
          .join(', ');
        return `{${props}}`;
      }

      // Block style for objects
      const indent = getIndent(level);

      return '\n' + sortedKeys
        .map((key: string) => {
          const keyStr = needsQuotes(key) ? `"${key}"` : key;
          const valueStr: string = valueToYaml(value[key], level + 1);

          if (valueStr === '') return ''; // Skip invalid values

          if (valueStr.startsWith('\n')) {
            return `${indent}${keyStr}:${valueStr}`;
          }

          return `${indent}${keyStr}: ${valueStr}`;
        })
        .filter((line: string) => line !== '')
        .join('\n');
    }

    return String(value);
  };

  // Convert JSON to YAML
  const convertJsonToYaml = () => {
    setError('');
    setIsLoading(true);
    setIsYamlGenerated(false);
    setYamlOutput('');

    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      setIsLoading(false);
      return;
    }

    if (!validateJson(jsonInput)) {
      setError('Invalid JSON format. Please check your input.');
      setIsLoading(false);

      trackEvent('conversion_error', {
        event_category: 'error',
        event_label: 'Invalid JSON Format',
        page_location: window.location.href,
      });
      return;
    }

    try {
      const jsonData = JSON.parse(jsonInput);

      // YAML header
      let yamlHeader = '';
      if (explicitStart || yamlFormat !== 'default') {
        if (yamlFormat === 'yaml1.1') {
          yamlHeader = '%YAML 1.1\n---\n';
        } else if (yamlFormat === 'yaml1.2') {
          yamlHeader = '%YAML 1.2\n---\n';
        } else if (yamlFormat === 'json') {
          yamlHeader = '%YAML 1.2\n%TAG ! tag:yaml.org,2002:\n---\n';
        } else if (explicitStart) {
          yamlHeader = '---\n';
        }
      }

      // Generate YAML content
      const yamlContent = valueToYaml(jsonData, 0).trim();

      // YAML footer
      let yamlFooter = '';
      if (explicitEnd) {
        yamlFooter = '\n...';
      }

      // Combine all parts
      const yaml = yamlHeader + yamlContent + yamlFooter;

      setYamlOutput(yaml);
      setIsYamlGenerated(true);
      setIsLoading(false);

      // Update analytics
      const fileSize = new Blob([yaml]).size / 1024; // Size in KB
      updateAnalytics('conversion', { fileSize });

      trackEvent('convert_json_to_yaml', {
        event_category: 'conversion',
        event_label: 'Successful Conversion',
        page_location: window.location.href,
      });
    } catch (e) {
      const errorMessage = `Error generating YAML: ${e instanceof Error ? e.message : String(e)}`;
      setError(errorMessage);
      setIsLoading(false);

      trackEvent('conversion_error', {
        event_category: 'error',
        event_label: errorMessage,
        page_location: window.location.href,
      });
    }
  };

  // Copy to clipboard
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(yamlOutput);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);

      updateAnalytics('copy');

      trackEvent('copy_yaml', {
        event_category: 'user_interaction',
        event_label: 'YAML Copied to Clipboard',
        page_location: window.location.href,
      });
    } catch (error) {
      setError('Failed to copy to clipboard');

      trackEvent('copy_error', {
        event_category: 'error',
        event_label: 'Failed to Copy to Clipboard',
        page_location: window.location.href,
      });
    }
  };

  // Download YAML
  const downloadYaml = () => {
    try {
      const blob = new Blob([yamlOutput], { type: 'text/yaml' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = `${fileName || 'data'}.yaml`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      updateAnalytics('download');

      trackEvent('download_yaml', {
        event_category: 'user_interaction',
        event_label: 'YAML File Downloaded',
        page_location: window.location.href,
      });
    } catch (error) {
      setError('Failed to download file');

      trackEvent('download_error', {
        event_category: 'error',
        event_label: 'Failed to Download File',
        page_location: window.location.href,
      });
    }
  };

  return (
    <div
      ref={containerRef}
      className={`min-h-screen transition-all duration-300 ${isDarkMode ? 'dark bg-gray-900' : 'bg-gradient-to-br from-gray-50 to-blue-50'
        } ${isFullscreen ? 'p-0' : 'p-4'}`}
    >
      <SEOHead
        title="Professional JSON to YAML Converter - Free Online Tool with Advanced Features"
        description="Convert JSON to YAML format instantly with our professional online tool. Features advanced options, dark/light themes, analytics dashboard, fullscreen mode, and comprehensive tracking. Free and secure data conversion."
        keywords="json to yaml, yaml converter, json converter, data conversion, online tool, yaml generator, json parser, yaml formatter, data transformation, configuration converter"
      />

      <div className={`${isFullscreen ? 'max-w-none h-screen' : 'max-w-7xl'} mx-auto`}>
        {/* Header */}
        <div className="flex justify-between items-center mb-6 bg-white/70 dark:bg-gray-800/70 backdrop-blur-lg rounded-xl shadow-lg border border-white/20 dark:border-gray-700/50 p-6">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent flex items-center">
              <FileText className="mr-4 text-blue-600 dark:text-blue-400" size={32} />
              JSON to YAML Converter
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2 text-lg">
              Professional data conversion tool with advanced features and analytics
            </p>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowAnalytics(true)}
              className="p-3 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 
                       hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl transition-all duration-200 
                       border border-transparent hover:border-blue-200 dark:hover:border-blue-800"
              title="View Analytics Dashboard"
            >
              <BarChart3 className="w-6 h-6" />
            </button>

            <button
              onClick={toggleTheme}
              className="p-3 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 
                       hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl transition-all duration-200 
                       border border-transparent hover:border-blue-200 dark:hover:border-blue-800"
              title={`Switch to ${isDarkMode ? 'Light' : 'Dark'} Mode`}
            >
              {isDarkMode ? <Sun className="w-6 h-6" /> : <Moon className="w-6 h-6" />}
            </button>

            <button
              onClick={toggleFullscreen}
              className="p-3 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 
                       hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl transition-all duration-200 
                       border border-transparent hover:border-blue-200 dark:hover:border-blue-800"
              title={`${isFullscreen ? 'Exit' : 'Enter'} Fullscreen Mode`}
            >
              {isFullscreen ? <Minimize className="w-6 h-6" /> : <Maximize className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Description Banner */}
        <div className="mb-8 bg-white border border-black rounded-xl p-6">
          <div className="flex items-start space-x-4">
            <Info className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
            <div>
              <h2 className="text-xl font-semibold text-black mb-2">
                Transform Your Data Effortlessly
              </h2>
              <p className="text-black leading-relaxed">
                Convert JSON data to YAML format with professional-grade customization options.
                Features advanced formatting controls, real-time analytics, theme modes, fullscreen editing,
                and comprehensive activity tracking for optimal productivity.
              </p>
            </div>
          </div>
        </div>

        <div className={`grid ${isFullscreen ? 'grid-cols-1 lg:grid-cols-2 h-[calc(100vh-240px)]' : 'grid-cols-1 xl:grid-cols-2'} gap-6`}>
          {/* Input Section */}
          <div className="flex flex-col space-y-6">
            {/* JSON Input Panel */}
            <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-lg rounded-xl shadow-lg border border-white/20 dark:border-gray-700/50 flex-grow flex flex-col">
              <div className="p-4 border-b border-gray-200/50 dark:border-gray-700/50">
                <div className="flex justify-between items-center">
                  <label className="font-semibold text-gray-800 dark:text-gray-200 flex items-center text-lg">
                    <Zap className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
                    JSON Input
                  </label>
                  <div className="flex space-x-2">
                    <button
                      className="px-3 py-1.5 text-sm bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg 
                               hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-sm"
                      onClick={useExampleData}
                    >
                      Use Example
                    </button>
                    <button
                      className="px-3 py-1.5 text-sm bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg 
                               hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-sm"
                      onClick={beautifyJson}
                    >
                      Format JSON
                    </button>
                    <button
                      className="px-3 py-1.5 text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all duration-200 shadow-sm"
                      onClick={() => setJsonInput('')}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-4 flex-grow flex flex-col">
                <textarea
                  className="w-full flex-grow p-4 border border-gray-300/50 dark:border-gray-600/50 rounded-xl font-mono text-sm 
                           bg-gray-50/50 dark:bg-gray-900/50 text-gray-900 dark:text-gray-100
                           focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 
                           resize-none transition-all duration-200 backdrop-blur-sm"
                  value={jsonInput}
                  onChange={handleInputChange}
                  placeholder='Paste your JSON data here...'
                  style={{ minHeight: isFullscreen ? '300px' : '400px' }}
                />
              </div>
            </div>

            {/* Configuration Panel */}
            <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-lg rounded-xl shadow-lg border border-white/20 dark:border-gray-700/50">
              <div className="p-4 border-b border-gray-200/50 dark:border-gray-700/50">
                <h3 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center text-lg">
                  <Settings className="w-5 h-5 mr-2 text-gray-600 dark:text-gray-400" />
                  Configuration Options
                </h3>
              </div>

              <div className="p-4 space-y-4">
                {/* File Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Output File Name
                  </label>
                  <input
                    type="text"
                    className="w-full p-3 border border-gray-300/50 dark:border-gray-600/50 rounded-lg 
                             bg-white/50 dark:bg-gray-900/50 text-gray-900 dark:text-gray-100
                             focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400
                             transition-all duration-200"
                    value={fileName}
                    onChange={(e) => setFileName(e.target.value)}
                    placeholder="Enter file name (without extension)"
                  />
                </div>

                {/* Basic Options Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      YAML Format
                    </label>
                    <select
                      className="w-full p-3 border border-gray-300/50 dark:border-gray-600/50 rounded-lg 
                               bg-white/50 dark:bg-gray-900/50 text-gray-900 dark:text-gray-100
                               focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400
                               transition-all duration-200"
                      value={yamlFormat}
                      onChange={(e) => setYamlFormat(e.target.value as YamlFormat)}
                    >
                      <option value="default">Default (no header)</option>
                      <option value="yaml1.1">YAML 1.1</option>
                      <option value="yaml1.2">YAML 1.2</option>
                      <option value="json">JSON Schema mode</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Indentation Size
                    </label>
                    <select
                      className="w-full p-3 border border-gray-300/50 dark:border-gray-600/50 rounded-lg 
                               bg-white/50 dark:bg-gray-900/50 text-gray-900 dark:text-gray-100
                               focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400
                               transition-all duration-200"
                      value={indentSize}
                      onChange={(e) => setIndentSize(Number(e.target.value) as IndentSize)}
                    >
                      <option value="2">2 spaces</option>
                      <option value="4">4 spaces</option>
                      <option value="8">8 spaces</option>
                    </select>
                  </div>
                </div>

                {/* Advanced Options Toggle */}
                <button
                  className="w-full flex items-center justify-center space-x-2 p-3 
                           bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 
                           text-gray-700 dark:text-gray-300 rounded-xl hover:from-gray-200 hover:to-gray-300 
                           dark:hover:from-gray-600 dark:hover:to-gray-500 transition-all duration-200 
                           border border-gray-300/50 dark:border-gray-600/50"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                >
                  <Settings className="w-4 h-4" />
                  <span className="font-medium">{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                  {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>

                {/* Advanced Options Panel */}
                {showAdvanced && (
                  <div className="space-y-4 p-4 bg-gradient-to-r from-gray-50/80 to-blue-50/80 dark:from-gray-700/50 dark:to-blue-900/20 
                                rounded-xl border border-gray-200/50 dark:border-gray-600/50 backdrop-blur-sm">

                    {/* Flow and Line Controls */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Flow Level
                        </label>
                        <select
                          className="w-full p-2.5 border border-gray-300/50 dark:border-gray-600/50 rounded-lg 
                                   bg-white/50 dark:bg-gray-900/50 text-gray-900 dark:text-gray-100
                                   focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400
                                   transition-all duration-200 text-sm"
                          value={flowLevel}
                          onChange={(e) => setFlowLevel(Number(e.target.value))}
                        >
                          <option value="-1">Auto (block style)</option>
                          <option value="0">Inline everything</option>
                          <option value="1">Block level 0, flow level 1+</option>
                          <option value="2">Block levels 0-1, flow level 2+</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Line Width
                        </label>
                        <input
                          type="number"
                          min="40"
                          max="200"
                          className="w-full p-2.5 border border-gray-300/50 dark:border-gray-600/50 rounded-lg 
                                   bg-white/50 dark:bg-gray-900/50 text-gray-900 dark:text-gray-100
                                   focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400
                                   transition-all duration-200 text-sm"
                          value={lineWidth}
                          onChange={(e) => setLineWidth(Number(e.target.value))}
                        />
                      </div>
                    </div>

                    {/* Boolean Options */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-2 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-all duration-200"
                              checked={quoteStrings}
                              onChange={(e) => setQuoteStrings(e.target.checked)}
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">Quote All Strings</span>
                          </label>
                        </div>

                        <div className="flex items-center justify-between p-2 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-all duration-200"
                              checked={sortKeys}
                              onChange={(e) => setSortKeys(e.target.checked)}
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">Sort Object Keys</span>
                          </label>
                        </div>

                        <div className="flex items-center justify-between p-2 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-all duration-200"
                              checked={allowUnicode}
                              onChange={(e) => setAllowUnicode(e.target.checked)}
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">Allow Unicode</span>
                          </label>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-2 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-all duration-200"
                              checked={preserveQuotes}
                              onChange={(e) => setPreserveQuotes(e.target.checked)}
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">Preserve Quotes</span>
                          </label>
                        </div>

                        <div className="flex items-center justify-between p-2 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-all duration-200"
                              checked={skipInvalid}
                              onChange={(e) => setSkipInvalid(e.target.checked)}
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">Skip Invalid Values</span>
                          </label>
                        </div>

                        <div className="flex items-center justify-between p-2 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-all duration-200"
                              checked={explicitStart}
                              onChange={(e) => setExplicitStart(e.target.checked)}
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">Explicit Start (---)</span>
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-2 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-all duration-200"
                          checked={explicitEnd}
                          onChange={(e) => setExplicitEnd(e.target.checked)}
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">Explicit End (...)</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex space-x-3 pt-2">
                  <button
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl 
                             hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 
                             focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-all duration-200 
                             disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2
                             shadow-lg hover:shadow-xl font-medium"
                    onClick={convertJsonToYaml}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        <span>Converting...</span>
                      </>
                    ) : (
                      <>
                        <Zap className="w-5 h-5" />
                        <span>Convert to YAML</span>
                      </>
                    )}
                  </button>

                  <button
                    className="px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-xl 
                             hover:from-gray-600 hover:to-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 
                             focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-all duration-200
                             shadow-lg hover:shadow-xl font-medium"
                    onClick={clearAll}
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Output Section */}
          <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-lg rounded-xl shadow-lg border border-white/20 dark:border-gray-700/50 flex flex-col">
            <div className="p-4 border-b border-gray-200/50 dark:border-gray-700/50">
              <div className="flex justify-between items-center">
                <label className="font-semibold text-gray-800 dark:text-gray-200 flex items-center text-lg">
                  <FileText className="w-5 h-5 mr-2 text-green-600 dark:text-green-400" />
                  YAML Output
                </label>

                {isYamlGenerated && (
                  <div className="flex space-x-2">
                    <button
                      className="flex items-center space-x-2 px-3 py-1.5 bg-gradient-to-r from-blue-500 to-blue-600 
                               text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 text-sm
                               shadow-sm hover:shadow-md"
                      onClick={copyToClipboard}
                    >
                      <Copy className="w-4 h-4" />
                      <span>{copied ? 'Copied!' : 'Copy'}</span>
                    </button>

                    <button
                      className="flex items-center space-x-2 px-3 py-1.5 bg-gradient-to-r from-green-500 to-green-600 
                               text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 text-sm
                               shadow-sm hover:shadow-md"
                      onClick={downloadYaml}
                    >
                      <Download className="w-4 h-4" />
                      <span>Download</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 flex-grow flex flex-col">
              {/* Error Display */}
              {error && (
                <div className="bg-gradient-to-r from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 
                              border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 p-4 rounded-xl mb-4
                              backdrop-blur-sm">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Conversion Error</p>
                      <p className="text-sm mt-1">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Empty State */}
              {!error && !isYamlGenerated && (
                <div className="bg-gradient-to-br from-gray-50/80 to-blue-50/80 dark:from-gray-900/50 dark:to-blue-900/20 
                              border border-gray-200/50 dark:border-gray-700/50 text-gray-500 dark:text-gray-400 
                              p-8 rounded-xl flex-grow flex items-center justify-center backdrop-blur-sm">
                  <div className="text-center">
                    <Eye className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500 mb-4" />
                    <h3 className="text-xl font-medium mb-2">YAML Preview</h3>
                    <p className="text-sm leading-relaxed max-w-md">
                      Your converted YAML will appear here after processing.
                      Use the advanced options to customize the output format.
                    </p>
                  </div>
                </div>
              )}

              {/* Output Display */}
              {!error && isYamlGenerated && (
                <div className="flex-grow flex flex-col">
                  <textarea
                    className="w-full flex-grow p-4 border border-gray-300/50 dark:border-gray-600/50 rounded-xl font-mono text-sm 
                             bg-gray-50/50 dark:bg-gray-900/50 text-gray-900 dark:text-gray-100
                             focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:focus:ring-green-400 
                             resize-none transition-all duration-200 backdrop-blur-sm"
                    value={yamlOutput}
                    readOnly
                    style={{ minHeight: isFullscreen ? '300px' : '400px' }}
                  />

                  {/* Success Message */}
                  <div className="mt-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 
                                border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 p-4 rounded-xl
                                backdrop-blur-sm">
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-green-500" />
                      <div>
                        <p className="font-medium">Conversion Successful!</p>
                        <div className="text-sm mt-2 grid grid-cols-2 gap-2">
                          <div>✓ YAML generated with {indentSize}-space indentation</div>
                          <div>✓ File size: {(yamlOutput.length / 1024).toFixed(2)} KB</div>
                          <div>✓ Format: {yamlFormat === 'default' ? 'Standard YAML' : yamlFormat}</div>
                          <div>✓ Lines: {yamlOutput.split('\n').length}</div>
                          {sortKeys && <div>✓ Object keys sorted alphabetically</div>}
                          {quoteStrings && <div>✓ All strings quoted</div>}
                          {explicitStart && <div>✓ Explicit document start</div>}
                          {explicitEnd && <div>✓ Explicit document end</div>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Information Section */}
        {!isFullscreen && (
          <div className="mt-8 bg-white/70 dark:bg-gray-800/70 backdrop-blur-lg rounded-xl shadow-lg border border-white/20 dark:border-gray-700/50">
            <div className="p-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 flex items-center">
                <Info className="w-8 h-8 mr-4 text-blue-600 dark:text-blue-400" />
                About JSON to YAML Conversion
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-8">
                  <div>
                    <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center text-xl">
                      <Zap className="w-6 h-6 mr-3 text-blue-600 dark:text-blue-400" />
                      How This Converter Works
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-lg">
                      Our professional JSON to YAML converter transforms your JSON data into human-readable YAML format
                      with precision and reliability. YAML (YAML Ain't Markup Language) is extensively used for
                      configuration files, data exchange, and applications requiring human-readable data serialization.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center text-xl">
                      <Settings className="w-6 h-6 mr-3 text-green-600 dark:text-green-400" />
                      Professional Features
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-start space-x-3 p-3 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-900/10 dark:to-indigo-900/10 rounded-lg">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                        <div>
                          <span className="font-medium text-gray-800 dark:text-gray-200">Multiple YAML Versions:</span>
                          <span className="text-gray-600 dark:text-gray-300 ml-2">Support for YAML 1.1, 1.2, and JSON Schema modes with proper headers</span>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3 p-3 bg-gradient-to-r from-green-50/50 to-emerald-50/50 dark:from-green-900/10 dark:to-emerald-900/10 rounded-lg">
                        <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                        <div>
                          <span className="font-medium text-gray-800 dark:text-gray-200">Advanced Formatting:</span>
                          <span className="text-gray-600 dark:text-gray-300 ml-2">Flexible indentation, flow control, and line width management</span>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3 p-3 bg-gradient-to-r from-purple-50/50 to-pink-50/50 dark:from-purple-900/10 dark:to-pink-900/10 rounded-lg">
                        <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                        <div>
                          <span className="font-medium text-gray-800 dark:text-gray-200">Smart Options:</span>
                          <span className="text-gray-600 dark:text-gray-300 ml-2">Intelligent string quoting, key sorting, and Unicode handling</span>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3 p-3 bg-gradient-to-r from-orange-50/50 to-red-50/50 dark:from-orange-900/10 dark:to-red-900/10 rounded-lg">
                        <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></span>
                        <div>
                          <span className="font-medium text-gray-800 dark:text-gray-200">User Experience:</span>
                          <span className="text-gray-600 dark:text-gray-300 ml-2">Dark/light themes, fullscreen mode, and comprehensive analytics</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-8">
                  <div>
                    <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center text-xl">
                      <CheckCircle className="w-6 h-6 mr-3 text-purple-600 dark:text-purple-400" />
                      Pro Tips for Optimal Results
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-start space-x-3 p-3 bg-gradient-to-r from-purple-50/50 to-indigo-50/50 dark:from-purple-900/10 dark:to-indigo-900/10 rounded-lg">
                        <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                        <span className="text-gray-600 dark:text-gray-300">Use "Format JSON" to validate and beautify your input before conversion</span>
                      </div>
                      <div className="flex items-start space-x-3 p-3 bg-gradient-to-r from-blue-50/50 to-cyan-50/50 dark:from-blue-900/10 dark:to-cyan-900/10 rounded-lg">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                        <span className="text-gray-600 dark:text-gray-300">Enable "Sort Object Keys" for consistent, predictable output structure</span>
                      </div>
                      <div className="flex items-start space-x-3 p-3 bg-gradient-to-r from-green-50/50 to-teal-50/50 dark:from-green-900/10 dark:to-teal-900/10 rounded-lg">
                        <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                        <span className="text-gray-600 dark:text-gray-300">Use "Quote All Strings" to prevent YAML type interpretation issues</span>
                      </div>
                      <div className="flex items-start space-x-3 p-3 bg-gradient-to-r from-orange-50/50 to-yellow-50/50 dark:from-orange-900/10 dark:to-yellow-900/10 rounded-lg">
                        <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></span>
                        <span className="text-gray-600 dark:text-gray-300">Try fullscreen mode for comfortable editing of large datasets</span>
                      </div>
                      <div className="flex items-start space-x-3 p-3 bg-gradient-to-r from-pink-50/50 to-rose-50/50 dark:from-pink-900/10 dark:to-rose-900/10 rounded-lg">
                        <span className="w-2 h-2 bg-pink-500 rounded-full mt-2 flex-shrink-0"></span>
                        <span className="text-gray-600 dark:text-gray-300">Monitor your conversion patterns and productivity via the analytics dashboard</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center text-xl">
                      <BarChart3 className="w-6 h-6 mr-3 text-orange-600 dark:text-orange-400" />
                      YAML vs JSON Comparison
                    </h3>
                    <div className="bg-gradient-to-r from-gray-50/80 to-blue-50/80 dark:from-gray-700/50 dark:to-blue-900/20 p-6 rounded-xl border border-gray-200/50 dark:border-gray-600/50 backdrop-blur-sm">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                            <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                            YAML Advantages
                          </h4>
                          <ul className="space-y-2 text-sm">
                            <li className="flex items-center space-x-2">
                              <span className="w-1 h-1 bg-green-500 rounded-full"></span>
                              <span className="text-gray-600 dark:text-gray-300">Human-readable indented structure</span>
                            </li>
                            <li className="flex items-center space-x-2">
                              <span className="w-1 h-1 bg-green-500 rounded-full"></span>
                              <span className="text-gray-600 dark:text-gray-300">Native comment support</span>
                            </li>
                            <li className="flex items-center space-x-2">
                              <span className="w-1 h-1 bg-green-500 rounded-full"></span>
                              <span className="text-gray-600 dark:text-gray-300">No quotes required (usually)</span>
                            </li>
                            <li className="flex items-center space-x-2">
                              <span className="w-1 h-1 bg-green-500 rounded-full"></span>
                              <span className="text-gray-600 dark:text-gray-300">Cleaner, more readable syntax</span>
                            </li>
                            <li className="flex items-center space-x-2">
                              <span className="w-1 h-1 bg-green-500 rounded-full"></span>
                              <span className="text-gray-600 dark:text-gray-300">Ideal for configuration files</span>
                            </li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                            <Zap className="w-4 h-4 mr-2 text-blue-500" />
                            JSON Advantages
                          </h4>
                          <ul className="space-y-2 text-sm">
                            <li className="flex items-center space-x-2">
                              <span className="w-1 h-1 bg-blue-500 rounded-full"></span>
                              <span className="text-gray-600 dark:text-gray-300">Faster parsing performance</span>
                            </li>
                            <li className="flex items-center space-x-2">
                              <span className="w-1 h-1 bg-blue-500 rounded-full"></span>
                              <span className="text-gray-600 dark:text-gray-300">Universal language support</span>
                            </li>
                            <li className="flex items-center space-x-2">
                              <span className="w-1 h-1 bg-blue-500 rounded-full"></span>
                              <span className="text-gray-600 dark:text-gray-300">More compact file size</span>
                            </li>
                            <li className="flex items-center space-x-2">
                              <span className="w-1 h-1 bg-blue-500 rounded-full"></span>
                              <span className="text-gray-600 dark:text-gray-300">Perfect for REST APIs</span>
                            </li>
                            <li className="flex items-center space-x-2">
                              <span className="w-1 h-1 bg-blue-500 rounded-full"></span>
                              <span className="text-gray-600 dark:text-gray-300">Stricter, more predictable syntax</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Features Section */}
              <div className="mt-12 pt-8 border-t border-gray-200/50 dark:border-gray-700/50">
                <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-6 flex items-center text-2xl">
                  <Activity className="w-6 h-6 mr-3 text-indigo-600 dark:text-indigo-400" />
                  Advanced Analytics & Tracking
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-6 rounded-xl border border-blue-200/50 dark:border-blue-800/50">
                    <TrendingUp className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-3" />
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">Conversion Metrics</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Track total conversions, average file sizes, and processing patterns to optimize your workflow.</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-6 rounded-xl border border-green-200/50 dark:border-green-800/50">
                    <Activity className="w-8 h-8 text-green-600 dark:text-green-400 mb-3" />
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">Activity Timeline</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Detailed logging of all user interactions, downloads, and conversions with timestamps.</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-6 rounded-xl border border-purple-200/50 dark:border-purple-800/50">
                    <BarChart3 className="w-8 h-8 text-purple-600 dark:text-purple-400 mb-3" />
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">Usage Analytics</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Comprehensive insights into feature usage, preferences, and productivity patterns.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Analytics Modal */}
      <AnalyticsModal
        isOpen={showAnalytics}
        onClose={() => setShowAnalytics(false)}
        analytics={analytics}
      />
    </div>
  );
};

export default JsonToYamlConverter;